// src/pages/CompaniesPage.tsx
import { useEffect, useMemo, useState } from "react";
import { listCompanies, createCompany, deleteCompany } from "../services/companies";
import { listProjects, createProject, updateProject, deleteProject, getProjectsWithoutCompany } from "../services/projects";
import type { Company, ID, Project } from "../types/domain";
import { toast } from "react-hot-toast";
import { confirm } from "../lib/dialogs";
import { Link } from "react-router-dom";
import { FiBriefcase, FiFolder, FiPlus, FiTrash2, FiTrello, FiCalendar, FiBarChart, FiUsers, FiSettings, FiX, FiEdit3 } from "react-icons/fi";
import GlobalHeader from "../components/layout/GlobalHeader";
import GlobalSidebar from "../components/layout/GlobalSidebar";
import CompanyMembersModal from "../components/modals/CompanyMembersModal";
import ProjectMembersModal from "../components/modals/ProjectMembersModal";
import CompanyManagementSidebar from "../components/companies/CompanyManagementSidebar";
import TextInputModal from "../components/modals/TextInputModal";
import { useUserRole } from "../hooks/useUserRole";
import "./CompaniesPage.css";
import "../components/OrphanProjectModal.css";

export default function CompaniesPage() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [orphanProjects, setOrphanProjects] = useState<Project[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<ID | "">("");

  // formularios
  const [createCompanyName, setCreateCompanyName] = useState("");
  const [createProjectName, setCreateProjectName] = useState("");

  // Modal states
  const [membersModalOpen, setMembersModalOpen] = useState(false);
  const [membersModalCompany, setMembersModalCompany] = useState<Company | null>(null);
  const [projectMembersModalOpen, setProjectMembersModalOpen] = useState(false);
  const [projectMembersModalProject, setProjectMembersModalProject] = useState<Project | null>(null);
  const [managementSidebarOpen, setManagementSidebarOpen] = useState(false);
  const [managementSidebarCompany, setManagementSidebarCompany] = useState<Company | null>(null);
  const [orphanProjectModalOpen, setOrphanProjectModalOpen] = useState(false);
  const [orphanProjectToAssign, setOrphanProjectToAssign] = useState<Project | null>(null);
  
  // Text input modal states
  const [textInputOpen, setTextInputOpen] = useState(false);
  const [textInputConfig, setTextInputConfig] = useState({
    title: "",
    placeholder: "",
    initialValue: "",
    confirmText: "Guardar",
    onSave: (value: string) => {}
  });

  // User role hook
  const { userRole, isAdminOfCompany, canCreateProjects } = useUserRole();

  const currentCompany = useMemo(
    () => companies.find((c) => c.id === selectedCompany) || null,
    [companies, selectedCompany]
  );

  useEffect(() => {
    document.title = "TaskManager - Empresas";
    void loadCompanies();
    void loadOrphanProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany]);

  async function loadCompanies() {
    const { data, error } = await listCompanies();
    if (error) toast.error(error.message);
    setCompanies(data);
    if (!selectedCompany && data.length) setSelectedCompany(data[0].id);
  }

  async function loadProjects() {
    if (!selectedCompany) { setProjects([]); return; }
    const { data, error } = await listProjects(selectedCompany);
    if (error) toast.error(error.message);
    setProjects(data);
  }

  async function loadOrphanProjects() {
    const { data, error } = await getProjectsWithoutCompany();
    if (error) toast.error("Error cargando proyectos sin empresa");
    else setOrphanProjects(data);
  }

  async function onCreateCompany(e: React.FormEvent) {
    e.preventDefault();
    const name = createCompanyName.trim();
    if (!name) return;
    const { error } = await createCompany({ name });
    if (error) toast.error("No se pudo crear la compañía");
    else {
      toast.success("Compañía creada");
      setCreateCompanyName("");
      await loadCompanies();
    }
  }

  async function onCreateProject(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedCompany) {
      toast.error("Selecciona una empresa primero");
      return;
    }
    
    const name = createProjectName.trim();
    if (!name) {
      toast.error("Ingresa un nombre para el proyecto");
      return;
    }
    
    // Verificar permisos con más detalle
    console.log('Verificando permisos para crear proyecto:', {
      selectedCompany,
      canCreate: canCreateProjects(selectedCompany),
      userRole: userRole
    });
    
    if (!canCreateProjects(selectedCompany)) {
      toast.error("No tienes permisos para crear proyectos en esta empresa");
      return;
    }
    
    try {
      const { error } = await createProject({ company_id: selectedCompany, name });
      if (error) {
        console.error('Error creating project:', error);
        toast.error(`No se pudo crear el proyecto: ${error.message}`);
      } else {
        toast.success("Proyecto creado exitosamente");
        setCreateProjectName("");
        await loadProjects();
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      toast.error("Error inesperado al crear el proyecto");
    }
  }


  async function onDeleteCompany(id: ID) {
    const ok = await confirm({
      title: "Eliminar compañía",
      text: "Se eliminará la compañía y (según tus RLS) sus proyectos. ¿Continuar?",
      icon: "warning",
      confirmText: "Eliminar",
    });
    if (!ok) return;
    const { error } = await deleteCompany(id);
    if (error) toast.error("No se pudo eliminar");
    else {
      toast.success("Compañía eliminada");
      if (selectedCompany === id) setSelectedCompany("");
      await loadCompanies();
      setProjects([]);
    }
  }

  async function onRenameProject(id: ID, oldName: string) {
    setTextInputConfig({
      title: "Renombrar proyecto",
      placeholder: "Nuevo nombre del proyecto",
      initialValue: oldName,
      confirmText: "Renombrar",
      onSave: async (name: string) => {
        const { error } = await updateProject(id, { name: name.trim() });
        if (error) toast.error("No se pudo renombrar");
        else { toast.success("Proyecto actualizado"); await loadProjects(); }
      }
    });
    setTextInputOpen(true);
  }

  async function onDeleteProject(id: ID) {
    const ok = await confirm({
      title: "Eliminar proyecto",
      text: "Esta acción no se puede deshacer.",
      icon: "warning",
      confirmText: "Eliminar",
    });
    if (!ok) return;
    const { error } = await deleteProject(id);
    if (error) toast.error("No se pudo eliminar");
    else { 
      toast.success("Proyecto eliminado"); 
      await loadProjects();
      await loadOrphanProjects();
    }
  }

  function openMembersModal(company: Company) {
    setMembersModalCompany(company);
    setMembersModalOpen(true);
  }

  function closeMembersModal() {
    setMembersModalOpen(false);
    setMembersModalCompany(null);
  }

  function openProjectMembersModal(project: Project) {
    setProjectMembersModalProject(project);
    setProjectMembersModalOpen(true);
  }

  function closeProjectMembersModal() {
    setProjectMembersModalOpen(false);
    setProjectMembersModalProject(null);
  }

  function openManagementSidebar(company: Company) {
    setManagementSidebarCompany(company);
    setManagementSidebarOpen(true);
  }

  function closeManagementSidebar() {
    setManagementSidebarOpen(false);
    setManagementSidebarCompany(null);
  }

  function openOrphanProjectModal(project: Project) {
    setOrphanProjectToAssign(project);
    setOrphanProjectModalOpen(true);
  }

  function closeOrphanProjectModal() {
    setOrphanProjectModalOpen(false);
    setOrphanProjectToAssign(null);
  }

  async function assignProjectToCompany(projectId: string, companyId: string) {
    try {
      const { error } = await updateProject(projectId, { company_id: companyId });
      if (error) {
        toast.error('Error asignando proyecto a empresa');
      } else {
        toast.success('Proyecto asignado exitosamente');
        await loadOrphanProjects();
        await loadProjects();
        closeOrphanProjectModal();
      }
    } catch (error) {
      toast.error('Error inesperado');
    }
  }

  return (
    <>
      
      {/* Global Header */}
      <GlobalHeader
        viewType="dashboard"
        onUserClick={() => {}}
      />
      
      {/* Global Sidebar - ocultar sección de empresas/proyectos */}
      <GlobalSidebar
        projects={[]}
        companies={[]}
        tasks={[]}
        showFilters={false}
        showProjectStats={false}
        onProjectChange={() => {}}
        onCompanyChange={() => {}}
        onPriorityChange={() => {}}
        onAssigneeChange={() => {}}
        onSearchChange={() => {}}
        onClearFilters={() => {}}
      />

      <div className="jira-kanban-container">
        <div className="jira-main">
          <div className="companies-modern-layout">
            {/* Header Section */}
            <div className="companies-header">
              <div className="companies-header-content">
                <h1 className="companies-title">
                  <FiBriefcase size={32} />
                  Gestión de Empresas y Proyectos
                </h1>
                <p className="companies-subtitle">
                  Organiza tu trabajo por empresas y crea proyectos para cada una
                </p>
              </div>
              
              <div className="companies-stats">
                <div className="stat-card">
                  <div className="stat-number">{companies.length}</div>
                  <div className="stat-label">Empresas</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">{projects.length}</div>
                  <div className="stat-label">Proyectos</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">{orphanProjects.length}</div>
                  <div className="stat-label">Sin Empresa</div>
                </div>
              </div>
            </div>

            {/* Companies Grid */}
            <div className="companies-grid">
              {/* Create Company Card */}
              <div className="company-card create-card">
                <form className="create-company-form" onSubmit={onCreateCompany}>
                  <div className="create-icon">
                    <FiPlus size={32} />
                  </div>
                  <h3>Nueva Empresa</h3>
                  <input
                    className="create-input"
                    placeholder="Nombre de la empresa"
                    value={createCompanyName}
                    onChange={(e) => setCreateCompanyName(e.target.value)}
                    required
                  />
                  <button className="create-btn" type="submit" disabled={!createCompanyName.trim()}>
                    Crear Empresa
                  </button>
                </form>
              </div>

              {/* Existing Companies */}
              {companies.map((company) => (
                <div 
                  key={company.id} 
                  className={`company-card ${selectedCompany === company.id ? 'selected' : ''}`}
                  onClick={() => setSelectedCompany(company.id)}
                >
                  <div className="company-header">
                    <div className="company-icon">
                      <FiBriefcase size={24} />
                    </div>
                    <div className="company-actions">
                      <button 
                        className="action-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          openManagementSidebar(company);
                        }}
                        title="Gestionar empresa"
                      >
                        <FiSettings size={16} />
                      </button>
                      {isAdminOfCompany(company.id) && (
                        <button 
                          className="action-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            openMembersModal(company);
                          }}
                          title="Gestionar miembros"
                        >
                          <FiUsers size={16} />
                        </button>
                      )}
                      <button 
                        className="action-btn danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteCompany(company.id);
                        }}
                        title="Eliminar empresa"
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </div>
                  </div>
                  
                  <h3 className="company-name">{company.name}</h3>
                  <div className="company-info">
                    <span className="company-projects">
                      {projects.filter(p => p.company_id === company.id).length} proyectos
                    </span>
                    <span className="company-date">
                      {company.created_at ? new Date(company.created_at).toLocaleDateString() : ''}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Projects Section */}
            {selectedCompany && currentCompany && (
              <div className="projects-section">
                <div className="projects-header">
                  <h2 className="projects-title">
                    <FiFolder size={24} />
                    Proyectos de {currentCompany.name}
                  </h2>
                  
                  {canCreateProjects(selectedCompany) && (
                    <form className="create-project-form" onSubmit={onCreateProject}>
                      <input
                        className="project-input"
                        placeholder="Nombre del nuevo proyecto"
                        value={createProjectName}
                        onChange={(e) => setCreateProjectName(e.target.value)}
                        required
                      />
                      <button 
                        className="project-create-btn" 
                        type="submit" 
                        disabled={!createProjectName.trim()}
                      >
                        <FiPlus size={16} />
                        Crear Proyecto
                      </button>
                    </form>
                  )}
                </div>

                <div className="projects-grid">
                  {projects
                    .filter(p => p.company_id === selectedCompany)
                    .map((project) => (
                      <div key={project.id} className="project-card">
                        <div className="project-header">
                          <div className="project-icon">
                            <FiFolder size={20} />
                          </div>
                          <div className="project-actions">
                            {isAdminOfCompany(project.company_id) && (
                              <button 
                                className="action-btn"
                                onClick={() => openProjectMembersModal(project)}
                                title="Gestionar miembros del proyecto"
                              >
                                <FiUsers size={14} />
                              </button>
                            )}
                            <button 
                              className="action-btn"
                              onClick={() => onRenameProject(project.id, project.name)}
                              title="Renombrar proyecto"
                            >
                              <FiEdit3 size={14} />
                            </button>
                            <button 
                              className="action-btn danger"
                              onClick={() => onDeleteProject(project.id)}
                              title="Eliminar proyecto"
                            >
                              <FiTrash2 size={14} />
                            </button>
                          </div>
                        </div>
                        
                        <h4 className="project-name">{project.name}</h4>
                        <div className="project-info">
                          <span className="project-id">ID: {project.id.slice(0, 8)}</span>
                          <span className="project-date">
                            {project.created_at ? new Date(project.created_at).toLocaleDateString() : ''}
                          </span>
                        </div>
                        
                        <div className="project-actions-footer">
                          <Link 
                            to={`/kanban?projectId=${project.id}`} 
                            className="project-link kanban"
                          >
                            <FiTrello size={16} />
                            Kanban
                          </Link>
                          <Link 
                            to={`/calendar-v2?projectId=${project.id}`} 
                            className="project-link calendar"
                          >
                            <FiCalendar size={16} />
                            Calendar
                          </Link>
                          <Link 
                            to={`/gantt?projectId=${project.id}`} 
                            className="project-link gantt"
                          >
                            <FiBarChart size={16} />
                            Gantt
                          </Link>
                        </div>
                      </div>
                    ))}
                  
                  {projects.filter(p => p.company_id === selectedCompany).length === 0 && (
                    <div className="empty-projects">
                      <FiFolder size={48} />
                      <h3>Sin proyectos</h3>
                      <p>Esta empresa no tiene proyectos todavía. ¡Crea el primero!</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {!selectedCompany && (
              <div className="no-company-selected">
                <FiBriefcase size={64} />
                <h3>Selecciona una empresa</h3>
                <p>Haz clic en una empresa para ver y gestionar sus proyectos</p>
              </div>
            )}

            {/* Orphan Projects Section */}
            {orphanProjects.length > 0 && (
              <div className="orphan-projects-section">
                <div className="projects-header">
                  <h2 className="projects-title">
                    <FiFolder size={24} />
                    Proyectos sin Empresa ({orphanProjects.length})
                  </h2>
                </div>

                <div className="projects-grid">
                  {orphanProjects.map((project) => (
                    <div key={project.id} className="project-card orphan">
                      <div className="project-header">
                        <div className="project-icon">
                          <FiFolder size={20} />
                        </div>
                        <div className="project-actions">
                          <button 
                            className="action-btn"
                            onClick={() => openOrphanProjectModal(project)}
                            title="Asignar a empresa"
                          >
                            <FiBriefcase size={14} />
                          </button>
                          <button 
                            className="action-btn danger"
                            onClick={() => onDeleteProject(project.id)}
                            title="Eliminar proyecto"
                          >
                            <FiTrash2 size={14} />
                          </button>
                        </div>
                      </div>
                      
                      <h4 className="project-name">{project.name}</h4>
                      <div className="project-info">
                        <span className="project-status">Sin empresa</span>
                      </div>
                      
                      <div className="project-actions-footer">
                        <Link 
                          to={`/kanban?projectId=${project.id}`} 
                          className="project-link kanban"
                        >
                          <FiTrello size={16} />
                          Kanban
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Company Members Modal */}
      <CompanyMembersModal
        company={membersModalCompany}
        isOpen={membersModalOpen}
        onClose={closeMembersModal}
      />

      {/* Project Members Modal */}
      <ProjectMembersModal
        project={projectMembersModalProject}
        isOpen={projectMembersModalOpen}
        onClose={closeProjectMembersModal}
      />

      {/* Company Management Sidebar */}
      <CompanyManagementSidebar
        isOpen={managementSidebarOpen}
        onClose={closeManagementSidebar}
        selectedCompany={managementSidebarCompany}
        onCompanyUpdated={() => {
          loadCompanies();
          loadProjects();
          loadOrphanProjects();
        }}
      />

      {/* Orphan Project Assignment Modal */}
      {orphanProjectModalOpen && orphanProjectToAssign && (
        <div className="modal-overlay" onClick={closeOrphanProjectModal}>
          <div className="orphan-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Asignar Proyecto a Empresa</h3>
              <button className="close-btn" onClick={closeOrphanProjectModal}>
                <FiX size={18} />
              </button>
            </div>
            <div className="modal-content">
              <p>Proyecto: <strong>{orphanProjectToAssign.name}</strong></p>
              <div className="companies-list">
                {companies.map(company => (
                  <button
                    key={company.id}
                    className="company-option"
                    onClick={() => assignProjectToCompany(orphanProjectToAssign.id, company.id)}
                  >
                    <FiBriefcase size={16} />
                    {company.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Text Input Modal */}
      <TextInputModal
        isOpen={textInputOpen}
        onClose={() => setTextInputOpen(false)}
        onSave={textInputConfig.onSave}
        title={textInputConfig.title}
        placeholder={textInputConfig.placeholder}
        initialValue={textInputConfig.initialValue}
        confirmText={textInputConfig.confirmText}
      />
    </>
  );
}
